import React from 'react'
import { Button, Empty, Spin, Typography } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Socket } from 'socket.io-client'
import { Link, useLocation } from 'react-router-dom'
import chatArrowDownIcon from 'assets/icons/chat-arrow-down.svg'
import { chatApi } from 'api/chat'
import {
  IChat,
  IChatListUpdatedPayload,
  IChatMessage,
  IChatMessageCreatedPayload,
  IChatReadPayload,
  IChatUnreadCountUpdatedPayload,
  ISendChatMessageAck
} from 'models/chat'
import { TOKEN } from 'shared/constants/app'
import { createChatSocket } from 'shared/utils/chatSocket'
import { showMessage } from 'shared/utils/showMessage'
import { getSupportClientInfo } from 'shared/utils/supportClientInfo'
import { getProductEditPath } from 'routing/utils/getProductEditPath'
import { useChatScroll } from 'shared/hooks/useChatScroll'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { useNow } from 'shared/hooks/useNow'
import { useViewportFill } from 'shared/hooks/useViewportFill'
import { useAppDispatch, useAppSelector } from 'shared/hooks/useReduxHooks'
import { appSelectors } from 'store/app'
import {
  chatActions,
  chatSelectors,
  getChat,
  getChatMessages,
  getChatUnreadCount,
  getChats
} from 'store/chat'
import { ChatComposer } from './ChatComposer'
import { ChatMessageMedia } from './ChatMessageMedia'
import { ChatMessageMeta } from './ChatMessageMeta'
import { useChatAttachments } from './useChatAttachments'
import {
  formatDateTime,
  getChatProduct,
  getMessagePreview,
  hasMedia,
  getRequestErrorText,
  getProductImage,
  getUserName,
  getUserNameLines,
  isWithinEditWindow,
  replaceMessage
} from './utils'
import styles from './ChatsPage.module.scss'

const { Text, Title } = Typography
const MESSAGE_MAX_LENGTH = 3000
const SCROLL_LOCK_CLASS = 'chats-scroll-lock'
const SUPPORT_SCROLL_KEY = 'support'
const SUPPORT_TITLE = 'Команда поддержки'

const CHATS_PAGE_SIZE = 20
const ADMIN_CHATS_PAGE_SIZE = 50

interface IChatsPageProps {
  sellerId?: string
  readOnly?: boolean
}

export const ChatsPage: React.FC<IChatsPageProps> = ({
  sellerId,
  readOnly = false
}) => {
  const dispatch = useAppDispatch()
  const isMobile = useIsMobile()
  const location = useLocation()
  const chats = useAppSelector(chatSelectors.selectChats)
  const activeChat = useAppSelector(chatSelectors.selectActiveChat)
  const messages = useAppSelector(chatSelectors.selectMessages)
  const isChatsLoading = useAppSelector(chatSelectors.selectIsChatsLoading)
  const isMessagesLoading = useAppSelector(chatSelectors.selectIsMessagesLoading)
  const userId = useAppSelector(appSelectors.selectUserId)
  const now = useNow(30000)
  const socketRef = React.useRef<Socket | null>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  useViewportFill(wrapperRef)
  const listItemsRef = React.useRef<HTMLDivElement | null>(null)
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null)
  const activeChatIdRef = React.useRef<string | null>(null)
  const [messageText, setMessageText] = React.useState('')
  const [isSending, setIsSending] = React.useState(false)
  const [hasMoreChats, setHasMoreChats] = React.useState(true)
  const [isMoreChatsLoading, setIsMoreChatsLoading] = React.useState(false)
  // Диалог с поддержкой закреплён над списком и живёт отдельно: в `GET /chat` его нет
  const [supportChat, setSupportChat] = React.useState<IChat | null>(null)
  const [supportMessages, setSupportMessages] = React.useState<IChatMessage[]>([])
  const [isSupportOpen, setIsSupportOpen] = React.useState(false)
  const [isSupportLoading, setIsSupportLoading] = React.useState(false)
  const supportChatIdRef = React.useRef<string | null>(null)
  const isSupportOpenRef = React.useRef(false)
  const [editingMessage, setEditingMessage] = React.useState<IChatMessage | null>(null)
  // Недописанное сообщение: на время правки поле занято текстом исправляемого
  const draftRef = React.useRef('')
  const attachments = useChatAttachments(isSupportOpen ? 'support' : activeChat?._id || null)

  const product = React.useMemo(
    () => getChatProduct(activeChat, messages),
    [activeChat, messages]
  )
  const productImage = getProductImage(product)
  const productId = product?._id
  const dialogMessages = isSupportOpen ? supportMessages : messages
  const isDialogLoading = isSupportOpen ? isSupportLoading : isMessagesLoading
  // В обращении в поддержку продавец пишет от лица пользователя, а не магазина
  const isOwnMessage = React.useCallback(
    (message: IChatMessage) => (
      isSupportOpen ? message.sender_type === 'USER' : message.sender_type === 'SELLER'
    ),
    [isSupportOpen]
  )
  const {
    containerRef: messagesRef,
    newItemsCount: newMessagesCount,
    handleScroll: handleMessagesScroll,
    scrollToLatestItem: scrollToLatestMessage
  } = useChatScroll<IChatMessage>({
    items: dialogMessages,
    resetKey: isSupportOpen ? SUPPORT_SCROLL_KEY : activeChat?._id,
    isOwnItem: isOwnMessage
  })

  React.useEffect(() => {
    supportChatIdRef.current = supportChat?._id || null
  }, [supportChat?._id])

  React.useEffect(() => {
    isSupportOpenRef.current = isSupportOpen
  }, [isSupportOpen])

  React.useEffect(() => {
    dispatch(chatActions.setActiveChat(null))
    dispatch(chatActions.setMessages([]))
    setHasMoreChats(true)
    dispatch(getChats({ sellerId, adminMode: readOnly }))
    if (!readOnly) {
      dispatch(getChatUnreadCount())
    }
  }, [dispatch, readOnly, sellerId])

  React.useEffect(() => {
    if (readOnly) return

    chatApi
      .getSupportChat()
      .then(({ data }) => setSupportChat(data.chat))
      .catch(() => setSupportChat(null))
  }, [readOnly])

  const loadMoreChats = React.useCallback(async () => {
    const pageSize = readOnly ? ADMIN_CHATS_PAGE_SIZE : CHATS_PAGE_SIZE

    if (isMoreChatsLoading || !hasMoreChats || chats.length < pageSize) return
    if (readOnly && !sellerId) return

    setIsMoreChatsLoading(true)
    try {
      const response = readOnly && sellerId
        ? await chatApi.getAdminSellerChats(sellerId, chats.length, pageSize)
        : await chatApi.getChats(chats.length, pageSize)

      dispatch(chatActions.appendChats(response.data))
      if (response.data.length < pageSize) {
        setHasMoreChats(false)
      }
    } finally {
      setIsMoreChatsLoading(false)
    }
  }, [chats.length, dispatch, hasMoreChats, isMoreChatsLoading, readOnly, sellerId])

  React.useEffect(() => {
    const root = listItemsRef.current
    const target = loadMoreRef.current
    const pageSize = readOnly ? ADMIN_CHATS_PAGE_SIZE : CHATS_PAGE_SIZE
    if (!root || !target || !hasMoreChats || isMoreChatsLoading || isChatsLoading || chats.length < pageSize) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreChats()
        }
      },
      { root, rootMargin: '120px' }
    )

    observer.observe(target)

    return () => observer.disconnect()
  }, [chats.length, hasMoreChats, isChatsLoading, isMoreChatsLoading, loadMoreChats, readOnly])

  React.useEffect(() => {
    activeChatIdRef.current = activeChat?._id || null
  }, [activeChat?._id])

  // Класс вешаем на html: страница чатов на телефоне занимает ровно экран,
  // и прокрутка должна работать только внутри переписки
  React.useEffect(() => {
    document.documentElement.classList.add(SCROLL_LOCK_CLASS)

    return () => {
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS)
    }
  }, [])

  const appendSupportMessage = (message: IChatMessage) => {
    setSupportMessages((prev) => (
      prev.some((item) => item._id === message._id) ? prev : [...prev, message]
    ))
  }

  React.useEffect(() => {
    if (readOnly) return

    if (!localStorage.getItem(TOKEN)) return

    const socket = createChatSocket()
    socketRef.current = socket

    // Переподключение после обрыва: подписки на переписки остались в старом соединении,
    // а события, пришедшие без нас, потеряны — подписываемся и перечитываем
    const resync = () => {
      const activeChatId = activeChatIdRef.current
      const supportChatId = isSupportOpenRef.current ? supportChatIdRef.current : null

      chatApi
        .getChats(0, CHATS_PAGE_SIZE)
        .then(({ data }) => dispatch(chatActions.appendChats(data)))
        .catch(() => undefined)
      chatApi
        .getSupportChat()
        .then(({ data }) => setSupportChat(data.chat))
        .catch(() => undefined)

      if (activeChatId) {
        socket.emit('subscribe_to_chat', { chatId: activeChatId })
        socket.emit('mark_chat_read', { chatId: activeChatId })
        chatApi
          .getMessages(activeChatId)
          .then(({ data }) => {
            if (activeChatIdRef.current === activeChatId) dispatch(chatActions.setMessages(data))
          })
          .catch(() => undefined)
      }
      if (supportChatId) {
        socket.emit('subscribe_to_chat', { chatId: supportChatId })
        socket.emit('mark_chat_read', { chatId: supportChatId })
        chatApi
          .getMessages(supportChatId)
          .then(({ data }) => {
            if (isSupportOpenRef.current && supportChatIdRef.current === supportChatId) {
              setSupportMessages(data)
            }
          })
          .catch(() => undefined)
      }
    }

    let hasConnected = false
    socket.on('connect', () => {
      if (hasConnected) resync()
      hasConnected = true
    })
    socket.on('chat_list_updated', (payload: IChatListUpdatedPayload) => {
      if (payload.chat.type === 'SUPPORT' || payload.chat._id === supportChatIdRef.current) {
        return setSupportChat(payload.chat)
      }
      dispatch(chatActions.applyChatListUpdated(payload))
    })
    socket.on('chat_unread_count_updated', (payload: IChatUnreadCountUpdatedPayload) => {
      dispatch(chatActions.setUnreadCount(payload.unreadCount))
    })
    socket.on('chat_message_created', (payload: IChatMessageCreatedPayload) => {
      if (payload.chatId === supportChatIdRef.current) {
        appendSupportMessage(payload.message)
        if (isSupportOpenRef.current) {
          socket.emit('mark_chat_read', { chatId: payload.chatId })
        }
        dispatch(getChatUnreadCount())
        return
      }

      dispatch(chatActions.applyMessageCreated(payload))
      if (activeChatIdRef.current === payload.chatId) {
        socket.emit('mark_chat_read', { chatId: payload.chatId })
      }
      dispatch(getChatUnreadCount())
    })
    socket.on('chat_message_updated', (payload: IChatMessageCreatedPayload) => {
      if (payload.chatId === supportChatIdRef.current) {
        setSupportMessages((prev) => replaceMessage(prev, payload.message))
        return
      }
      dispatch(chatActions.applyMessageUpdated(payload))
    })
    socket.on('chat_read', (payload: IChatReadPayload) => {
      if (payload.chatId === activeChatIdRef.current) {
        dispatch(getChat(payload.chatId))
      }
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [dispatch, readOnly])

  const resetComposer = React.useCallback(() => {
    setEditingMessage(null)
    draftRef.current = ''
    setMessageText('')
  }, [])

  const handleSelectChat = (chatId: string) => {
    const socket = socketRef.current

    if (activeChat?._id) {
      socket?.emit('unsubscribe_from_chat', { chatId: activeChat._id })
    }
    if (supportChat?._id && isSupportOpen) {
      socket?.emit('unsubscribe_from_chat', { chatId: supportChat._id })
    }

    setIsSupportOpen(false)
    dispatch(chatActions.setMessages([]))
    resetComposer()
    dispatch(getChat({ chatId, adminMode: readOnly }))
    dispatch(getChatMessages({ chatId, adminMode: readOnly }))
    if (!readOnly) {
      socket?.emit('subscribe_to_chat', { chatId })
    }
    if (!readOnly) {
      socket?.emit('mark_chat_read', { chatId })
    }
  }

  const openSupportChat = React.useCallback(async () => {
    const socket = socketRef.current

    if (activeChatIdRef.current) {
      socket?.emit('unsubscribe_from_chat', { chatId: activeChatIdRef.current })
    }

    dispatch(chatActions.setActiveChat(null))
    dispatch(chatActions.setMessages([]))
    resetComposer()
    setIsSupportOpen(true)
    setIsSupportLoading(true)

    try {
      const { data } = await chatApi.getSupportChat()
      setSupportChat(data.chat)

      if (!data.chat) {
        setSupportMessages([])
        return
      }

      const messagesResponse = await chatApi.getMessages(data.chat._id)
      setSupportMessages(messagesResponse.data)
      socket?.emit('subscribe_to_chat', { chatId: data.chat._id })
      socket?.emit('mark_chat_read', { chatId: data.chat._id })
      dispatch(getChatUnreadCount())
    } finally {
      setIsSupportLoading(false)
    }
  }, [dispatch, resetComposer])

  // Вкладка «Поддержка» в шапке открывает раздел сообщений сразу на этом диалоге
  React.useEffect(() => {
    if (readOnly || !(location.state as { openSupport?: boolean } | null)?.openSupport) return

    openSupportChat()
  }, [location.key, openSupportChat, readOnly])

  const handleBackToList = () => {
    const socket = socketRef.current

    if (activeChat?._id) {
      socket?.emit('unsubscribe_from_chat', { chatId: activeChat._id })
    }
    if (supportChat?._id && isSupportOpen) {
      socket?.emit('unsubscribe_from_chat', { chatId: supportChat._id })
    }

    setIsSupportOpen(false)
    dispatch(chatActions.setActiveChat(null))
    dispatch(chatActions.setMessages([]))
    resetComposer()
  }

  const addAckMessage = (message?: IChatMessage | string) => {
    if (message && typeof message !== 'string') {
      dispatch(chatActions.addMessage(message))
    }
  }

  const handleSendToSupport = async (text: string, images: File[], videos: File[]) => {
    const isNewChat = !supportChatIdRef.current

    setIsSending(true)
    try {
      const { data } = await chatApi.sendSupportMessage({ text, client: getSupportClientInfo() }, { images, videos })
      setSupportChat(data.chat)
      appendSupportMessage(data.message)
      setMessageText('')
      attachments.clear()
      socketRef.current?.emit('subscribe_to_chat', { chatId: data.chat._id })

      // Приветствие поддержки приходит сразу, а подписки на только что созданный чат ещё не было
      if (isNewChat) {
        const messagesResponse = await chatApi.getMessages(data.chat._id)
        setSupportMessages(messagesResponse.data)
      }
    } catch (e) {
      showMessage('error', 'Сообщение не отправлено', getRequestErrorText(e))
    } finally {
      setIsSending(false)
    }
  }

  const handleSend = async () => {
    const trimmedText = messageText.trim()
    const { images, videos } = attachments
    if ((!trimmedText && !images.length && !videos.length) || isSending) return
    if (trimmedText.length > MESSAGE_MAX_LENGTH) return

    if (isSupportOpen) {
      return handleSendToSupport(trimmedText, images, videos)
    }

    if (!activeChat?._id || !productId) return

    setIsSending(true)
    const socket = socketRef.current

    // Сокет файлы не возит: сообщение с фото или видео уходит запросом
    if (socket?.connected && !images.length && !videos.length) {
      socket.emit(
        'send_chat_message',
        { chatId: activeChat._id, productId, text: trimmedText },
        (response: ISendChatMessageAck) => {
          setIsSending(false)
          if (!response.success) return
          addAckMessage(response.message)
          setMessageText('')
        }
      )
      return
    }

    try {
      const response = await chatApi.sendMessage(activeChat._id, {
        product_id: productId,
        text: trimmedText
      }, { images, videos })
      dispatch(chatActions.addMessage(response.data))
      setMessageText('')
      attachments.clear()
    } catch (e) {
      showMessage('error', 'Сообщение не отправлено', getRequestErrorText(e))
    } finally {
      setIsSending(false)
    }
  }

  // В обращении в поддержку все сообщения пользователя — его; в переписке
  // с покупателем у магазина бывает несколько сотрудников, правят только своё
  const canEditMessage = (message: IChatMessage) =>
    !readOnly &&
    !message.is_auto_reply &&
    !message.is_internal &&
    isOwnMessage(message) &&
    (isSupportOpen || (Boolean(userId) && message.sender_user_id === userId)) &&
    isWithinEditWindow(message, now)

  const handleStartEdit = (message: IChatMessage) => {
    if (!editingMessage) {
      draftRef.current = messageText
    }
    setEditingMessage(message)
    setMessageText(message.text)
  }

  const handleCancelEdit = () => {
    setEditingMessage(null)
    setMessageText(draftRef.current)
    draftRef.current = ''
  }

  const handleSaveEdit = async () => {
    const trimmedText = messageText.trim()
    const chatId = isSupportOpen ? supportChat?._id : activeChat?._id
    if (!editingMessage || !chatId || isSending) return
    if (!trimmedText && !hasMedia(editingMessage)) return
    if (trimmedText === editingMessage.text) {
      handleCancelEdit()
      return
    }

    setIsSending(true)
    try {
      const { data } = await chatApi.editMessage(chatId, editingMessage._id, trimmedText)
      if (isSupportOpen) {
        setSupportMessages((prev) => replaceMessage(prev, data))
      } else {
        dispatch(chatActions.applyMessageUpdated({ chatId, message: data }))
      }
      handleCancelEdit()
    } catch (e) {
      showMessage('error', 'Сообщение не изменено', getRequestErrorText(e))
    } finally {
      setIsSending(false)
    }
  }

  const isDialogOpen = Boolean(activeChat) || isSupportOpen
  const isDialogOpenOnMobile = isMobile && isDialogOpen
  // Без товара нельзя отправить новое сообщение покупателю, но поправить отправленное можно
  const isSendDisabled = isSending || (!isSupportOpen && !editingMessage && !productId)
  const supportUnreadCount = supportChat?.unread_for_user || 0

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={`${styles.list} ${isDialogOpenOnMobile ? styles.hidden : ''}`}>
        <div className={styles.listHeader}>
          <Title level={4} style={{ margin: 0 }}>Диалоги</Title>
        </div>
        <div className={styles.listItems} ref={listItemsRef}>
          {!readOnly && (
            <div
              className={`${styles.chatItem} ${isSupportOpen ? styles.chatItemActive : ''}`}
              onClick={openSupportChat}
            >
              <div className={styles.chatItemTop}>
                <span className={styles.userName}>{SUPPORT_TITLE}</span>
                <span className={styles.date}>{formatDateTime(supportChat?.last_message_at)}</span>
              </div>
              <div className={styles.lastMessageWrapper}>
                <p className={styles.lastMessage}>
                  {supportChat?.last_message_text || 'Напишите нам, если нужна помощь'}
                </p>
                {supportUnreadCount > 0 && (
                  <span className={styles.unreadBadge}>{supportUnreadCount}</span>
                )}
              </div>
            </div>
          )}
          {isChatsLoading && !chats.length ? (
            <div className={styles.empty}><Spin /></div>
          ) : chats.length ? (
            <>
              {chats.map((chat) => {
                const unreadCount = chat.unread_for_seller || 0
                const itemProductImage = getProductImage(chat.last_product)
                return (
                  <div
                    key={chat._id}
                    className={`${styles.chatItem} ${activeChat?._id === chat._id ? styles.chatItemActive : ''}`}
                    onClick={() => handleSelectChat(chat._id)}
                  >
                    <div className={styles.chatItemTop}>
                      <span className={styles.userName}>{getUserName(chat.user_id)}</span>
                      <span className={styles.date}>{formatDateTime(chat.last_message_at)}</span>
                    </div>
                    {chat.last_product && (
                      <div className={styles.productLine}>
                        {itemProductImage && (
                          <img
                            src={itemProductImage}
                            alt={chat.last_product.name}
                            className={styles.productImage}
                          />
                        )}
                        <span className={styles.productName}>{chat.last_product.name}</span>
                      </div>
                    )}
                    <div className={styles.lastMessageWrapper}>
                      <p className={styles.lastMessage}>{chat.last_message_text || 'Нет сообщений'}</p>
                      {unreadCount > 0 && (
                        <span className={styles.unreadBadge}>{unreadCount}</span>
                      )}
                    </div>
                  </div>
                )
              })}
              <div ref={loadMoreRef} className={styles.loadMoreMarker} />
              {isMoreChatsLoading && <div className={styles.moreLoader}><Spin /></div>}
            </>
          ) : (
            <div className={styles.empty}><Empty description='Нет сообщений' /></div>
          )}
        </div>
      </div>

      <div className={`${styles.dialog} ${isMobile && !isDialogOpen ? styles.hidden : ''}`}>
        {!isDialogOpen ? (
          <div className={styles.empty}><Empty description='Выберите чат' /></div>
        ) : (
          <>
            <div className={styles.dialogHeader}>
              {isMobile && (
                <Button
                  type='text'
                  size='small'
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBackToList}
                  className={styles.backButton}
                >
                  К диалогам
                </Button>
              )}
              <div className={styles.headerInfo}>
                <Title level={4} className={styles.headerTitle}>
                  {isSupportOpen
                    ? SUPPORT_TITLE
                    : getUserNameLines(activeChat?.user_id).map((line, index) => (
                      <span key={`${index}${line}`} className={styles.headerTitleLine}>{line}</span>
                    ))}
                </Title>
                <Text className={styles.headerSubtitle}>
                  {isSupportOpen ? 'Поддержка HorseSmart' : 'Диалог с покупателем'}
                </Text>
              </div>
              {!isSupportOpen && product && (
                <div className={styles.headerProduct}>
                  {productImage && (
                    <Link to={getProductEditPath(product._id)} className={styles.productImageLink}>
                      <img src={productImage} alt={product.name} className={styles.productImage} />
                    </Link>
                  )}
                  <div className={styles.headerProductText}>
                    <Text type='secondary' className={styles.headerProductLabel}>
                      Последний товар
                    </Text>
                    <Link to={getProductEditPath(product._id)} className={styles.productName}>
                      {product.name}
                    </Link>
                    {!isMobile && <Link to={getProductEditPath(product._id)}>Открыть товар</Link>}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.messages} ref={messagesRef} onScroll={handleMessagesScroll}>
              {isDialogLoading ? (
                <Spin />
              ) : dialogMessages.length ? (
                dialogMessages.map((message) => (
                  <div
                    key={message._id}
                    className={`${styles.messageRow} ${isOwnMessage(message) ? styles.own : ''}`}
                  >
                    <div className={styles.bubble}>
                      {hasMedia(message) && <ChatMessageMedia images={message.images} videos={message.videos} />}
                      {message.text && <p className={styles.messageText}>{message.text}</p>}
                      <ChatMessageMeta
                        message={message}
                        onEdit={canEditMessage(message) ? () => handleStartEdit(message) : undefined}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <Empty description='Сообщений пока нет' />
              )}
            </div>
            {newMessagesCount > 0 && (
              <button
                type='button'
                className={styles.scrollToBottomButton}
                onClick={() => scrollToLatestMessage()}
                aria-label='Перейти к новым сообщениям'
              >
                <img src={chatArrowDownIcon} alt='' />
                <span className={styles.scrollToBottomBadge}>{newMessagesCount > 99 ? '99+' : newMessagesCount}</span>
              </button>
            )}
            {!readOnly && (
              <ChatComposer
                value={messageText}
                onChange={setMessageText}
                onSubmit={editingMessage ? handleSaveEdit : handleSend}
                placeholder={
                  isSupportOpen
                    ? 'Сообщение в поддержку'
                    : productId ? 'Сообщение покупателю' : 'Нет товара для ответа'
                }
                maxLength={MESSAGE_MAX_LENGTH}
                disabled={isSendDisabled}
                isSubmitting={isSending}
                editingText={editingMessage ? getMessagePreview(editingMessage) : null}
                onCancelEdit={handleCancelEdit}
                allowEmptyText={editingMessage ? hasMedia(editingMessage) : attachments.attachments.length > 0}
                attachments={attachments.attachments}
                onAttach={attachments.add}
                onRemoveAttachment={attachments.remove}
                onPaste={attachments.handlePaste}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
